import React, {useState, useEffect} from 'react';
import {Link, useHistory} from 'react-router-dom';
import toast from 'react-hot-toast';
import {useMutation} from 'react-query';
import moment from 'moment';
import {Eye, EyeOff} from 'react-feather';

import '../../index.css';

import {Input, Checkbox, Button} from '@components';
import {I18N_EN, I18N_ES_MX} from '@constants/languages';
import {registerApi} from '@apis';
import useAuth from '@context/useAuth';

import logo from '@images/nls-logo.png';
import {t} from 'i18next';
import {useTranslation} from 'react-i18next';

const Register = () => {
  const {i18n} = useTranslation();
  const {onRegister} = useAuth();
  const [state, setState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTos: false,
    showTerms: false,
  });
  const [signupPasswordVisibility, setSignupPasswordVisibility] = useState(
    false,
  );
  const [signupCPasswordVisibility, setSignupCPasswordVisibility] = useState(
    false,
  );

  const history = useHistory();

  const register = useMutation(params => registerApi(params));

  useEffect(() => {
    if (register?.isError) {
      let errorMessage = t('auth.registerError'); // Default generic error

      // register.error should now be the string message from the Promise.reject in doFetch
      const errorFromMutation = register.error;

      if (
        typeof errorFromMutation === 'string' &&
        errorFromMutation.trim() !== ''
      ) {
        errorMessage = errorFromMutation;
      } else if (errorFromMutation && typeof errorFromMutation === 'object') {
        // Fallback if error is an object (e.g. network error not caught by doFetch's specific handling)
        // or if doFetch somehow still sends a structured error for some cases.
        // Attempt to get a message from common error structures.
        const errorData =
          errorFromMutation?.response?.data ||
          errorFromMutation?.data ||
          errorFromMutation;
        if (
          errorData?.email &&
          Array.isArray(errorData.email) &&
          errorData.email.length > 0
        ) {
          errorMessage = errorData.email[0];
        } else if (errorData?.detail) {
          // Django REST Framework often uses 'detail'
          errorMessage = errorData.detail;
        } else if (errorFromMutation.message) {
          // Standard Error object 'message' property
          errorMessage = errorFromMutation.message;
        }
      }

      toast.error(errorMessage);
      if (register.reset) {
        register.reset();
      }
      return; // Exit early after handling the error to prevent navigation
    }

    if (register?.isSuccess) {
      onRegister({token: register?.data?.api_token});
      history.replace('/');
    }
  }, [history, onRegister, register, t]);

  const handleRegister = async e => {
    e.preventDefault();
    const {email, password, firstName, lastName, confirmPassword} = state;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      await toast.error(t('auth.enterEmailAddress'));
      return false;
    }
    if (password !== confirmPassword) {
      await toast.error(t('auth.passwordMatch'));
      return false;
    } else {
      await register.mutate({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleRegister(e);
    }
  };

  const showContent = () => {
    if (!state.showTerms) {
      return (
        <>
          <Input
            dark
            white
            value={state.firstName}
            onChange={e => setState({...state, firstName: e.target.value})}
            label={t('auth.firstName')}
            placeholder={t('auth.enterFirstName')}
          />
          <Input
            dark
            white
            value={state.lastName}
            onChange={e => setState({...state, lastName: e.target.value})}
            label={t('auth.lastName')}
            placeholder={t('auth.enterLastName')}
          />
          <Input
            type="email"
            dark
            white
            value={state.email}
            onChange={e => setState({...state, email: e.target.value})}
            label={t('auth.email')}
            placeholder={t('auth.enterEmail')}
          />

          <div className="position-rel">
            <Input
              dark
              white
              type={signupPasswordVisibility ? 'text' : 'password'}
              value={state.password}
              onChange={e => setState({...state, password: e.target.value})}
              label={t('auth.password')}
              placeholder={t('auth.enterPassword')}
            />
            <div
              className="password-toggle"
              onClick={() => {
                setSignupPasswordVisibility(!signupPasswordVisibility);
              }}>
              {signupPasswordVisibility ? (
                <Eye size="14" color="black" />
              ) : (
                <EyeOff size="14" color="black" />
              )}
            </div>
          </div>

          <div className="position-rel">
            <Input
              dark
              white
              type={signupCPasswordVisibility ? 'text' : 'password'}
              value={state.confirmPassword}
              onChange={e =>
                setState({...state, confirmPassword: e.target.value})
              }
              label={t('auth.confirmPassword')}
              placeholder={t('auth.confirmPassword')}
              onKeyDown={handleKeyDown}
            />
            <div
              className="password-toggle"
              onClick={() => {
                setSignupCPasswordVisibility(!signupCPasswordVisibility);
              }}>
              {signupCPasswordVisibility ? (
                <Eye size="14" color="black" />
              ) : (
                <EyeOff size="14" color="black" />
              )}
            </div>
          </div>
        </>
      );
    } else {
      return (
        <div className="loginTerms text-white">
          <h1 className="font-bold">{t('auth.termsTitle')}</h1>
          <p className="font-bold text-xs text-white">{t('auth.termsTitle')}</p>
          <div
            style={{
              maxHeight: 375,
              maxWidth: 425,
              overflowY: 'scroll',
              marginTop: 15,
              marginBottom: 15,
              scrollbarWidth: 1,
              paddingRight: 15,
            }}>
            <p className="font-light text-xs text-white">
              {t('auth.termsContent')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsPlatform')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsPlatformContent')}{' '}
              <b className="font-bold text-xs text-white">
                {t('auth.termsPlatformContent2')}
              </b>{' '}
              {t('auth.termsPlatformContents')}{' '}
              <b className="font-bold text-xs text-white">
                {t('auth.termsPlatformContent3')}
              </b>
              .
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsServices')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsServicesContent')}(
              <b className="font-bold text-xs text-white">
                {t('auth.termsServicesContent2')}
              </b>
              {t('auth.termsServicesContent3')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsRestrictions')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsRestrictionsContent')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsResponsibilities')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsResponsibilitiesContent')}
              <b className="font-bold text-xs text-white">
                {t('auth.termsResponsibilitiesContent2')}
              </b>{' '}
              {t('auth.termsResponsibilitiesContent3')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsAccount')}
            </p>
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsRegister')}
              </b>{' '}
              {t('auth.termsRegisterContent')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsAccountResponsibilities')}
              </b>{' '}
              {t('auth.termsAccountResponsibilitiesContent')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnership')}
            </p>
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipPlatform')}
              </b>{' '}
              {t('auth.termsOwnershipPlatformContent')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipApps')}
              </b>{' '}
              {t('auth.termsOwnershipAppsContent')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInvention')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent')}
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent2')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent3')}{' '}
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent4')}
              </b>
              {t('auth.termsOwnershipInventionContent5')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent6')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent7')}
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent8')}
              </b>
              {') '}
              {t('auth.termsOwnershipInventionContent9')}
            </p>
            <br />
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent10')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent11')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent12')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsOwnershipInventionContent13')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent14')}
            </p>
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">a.</b>{' '}
              {t('auth.termsOwnershipInventionContent15')}
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent16')}
              </b>
              {t('auth.termsOwnershipInventionContent17')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent18')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent19')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent20')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsOwnershipInventionContent21')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent22')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsOwnershipInventionContent23')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent24')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent25')}{' '}
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent26')}
              </b>
              :
            </p>
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">a.</b>
              {t('auth.termsOwnershipInventionContent27')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">b.</b>
              {t('auth.termsOwnershipInventionContent28')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">c.</b>
              {t('auth.termsOwnershipInventionContent29')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent30')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsOwnershipInventionContent31')}
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent32')}
              </b>
              {t('auth.termsOwnershipInventionContent33')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent34')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsOwnershipInventionContent35')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent36')}
            </p>
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">a.</b>
              {t('auth.termsOwnershipInventionContent37')}
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent38')}
              </b>
              {t('auth.termsOwnershipInventionContent39')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">b.</b>
              {t('auth.termsOwnershipInventionContent40')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent41')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsOwnershipInventionContent42')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent43')}
            </p>
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">a.</b>
              {t('auth.termsOwnershipInventionContent44')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">b.</b>
              {t('auth.termsOwnershipInventionContent45')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent46')}
            </p>
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                a. {t('auth.termsOwnershipInventionContent47')}
              </b>
              {t('auth.termsOwnershipInventionContent48')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">b.</b>{' '}
              {t('auth.termsOwnershipInventionContent49')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent50')}
            </p>
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent51')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent52')}{' '}
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent53')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent54')}
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent55')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent56')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent57')}
              </b>
              {t('auth.termsOwnershipInventionContent58')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent59')}
              </b>
              {t('auth.termsOwnershipInventionContent60')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent61')}{' '}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsOwnershipInventionContent62')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent63')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsOwnershipInventionContent64')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent65')}
            </p>
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent66')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent67')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent68')}{' '}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent69')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent70')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent71')}{' '}
              <b className="font-bold text-xs text-white underlined">
                {t('auth.termsOwnershipInventionContent72')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent73')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent74')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent75')}
            </p>
            <br />
            <p className="font-light text-xs text-white">
              <b className="font-bold text-xs text-white">
                {t('auth.termsOwnershipInventionContent76')}
              </b>{' '}
              {t('auth.termsOwnershipInventionContent77')}
            </p>
            <br />
            <p className="font-bold text-xs text-white">
              {t('auth.termsOwnershipInventionContent78')}
            </p>
            <p className="font-light text-xs text-white">
              {t('auth.termsOwnershipInventionContent79')}
            </p>
            <br />
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-6xl flex flex-col lg:flex-row bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Left Panel - Form */}
        <div className="flex-1 lg:w-auto next-gradient relative p-6 sm:p-8 lg:p-16 min-h-screen lg:min-h-0 flex flex-col justify-center">
          {/* Language Selector - Responsive positioning */}
          <div className="absolute top-4 left-4 right-4 flex justify-end space-x-2 z-10">
            <button
              className={`px-2 py-1 sm:px-3 sm:py-1 rounded text-xs sm:text-sm ${
                i18n.language === I18N_ES_MX
                  ? 'bg-white text-red-600'
                  : 'bg-transparent text-white border border-white'
              }`}
              onClick={() => i18n.changeLanguage(I18N_ES_MX)}>
              {t('auth.spanish')}
            </button>
            <button
              className={`px-2 py-1 sm:px-3 sm:py-1 rounded text-xs sm:text-sm ${
                i18n.language === I18N_EN
                  ? 'bg-white text-red-600'
                  : 'bg-transparent text-white border border-white'
              }`}
              onClick={() => i18n.changeLanguage(I18N_EN)}>
              {t('auth.english')}
            </button>
          </div>

          {/* Form Content */}
          <div className="w-full max-w-md mx-auto mt-12 lg:mt-0">
            <div className="text-center mb-8">
              <h1 className="font-bold text-2xl sm:text-3xl text-white">
                {t('auth.termsOwnershipInventionContent80')}
              </h1>
              <p className="font-light text-xs sm:text-sm text-white mt-3">
                {t('auth.termsOwnershipInventionContent81')}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              {showContent()}
              <div className="mt-4">
                <Checkbox
                  checked={state.agreeTos}
                  onChange={e =>
                    setState({...state, agreeTos: e.target.checked})
                  }
                  label={
                    <p className="text-white text-xs">
                      {t('auth.termsOwnershipInventionContent82')}
                      <Link
                        className="text-white text-xs px-2 underline"
                        onClick={() =>
                          setState({...state, showTerms: !state.showTerms})
                        }>
                        {t('auth.termsOwnershipInventionContent83')}
                      </Link>
                    </p>
                  }
                />
              </div>
            </div>

            <div className="space-y-4">
              <Button
                block
                onClick={handleRegister}
                loading={register?.isLoading}
                disabled={
                  register?.isLoading ||
                  !state.firstName ||
                  !state.lastName ||
                  !state.password ||
                  !state.confirmPassword ||
                  !state.agreeTos
                }
                label={t('auth.termsOwnershipInventionContent86')}
              />

              <div className="text-center">
                <p className="text-white text-xs">
                  {t('auth.termsOwnershipInventionContent84')}
                </p>
                <Link to="/" className="text-white text-xs underline ml-1">
                  {t('auth.termsOwnershipInventionContent85')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Logo (hidden on small screens, visible on lg+) */}
        <div className="hidden lg:flex lg:w-96 xl:w-[400px] justify-center items-center next-gradient p-8">
          <img
            className="object-contain max-w-full max-h-80"
            src={logo}
            alt="Next Level Technology Logo"
          />
        </div>
      </div>
    </div>
  );
};

export default Register;
